import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Select } from '@/components/ui';
import ComboBox from '@/components/ui/ComboBox';
import axios from 'axios';
import { fetchDivisions } from '@/store/slices/divisionsSlice';
import type { AppDispatch, RootState } from '@/store';
import type { Program, LeadType, User } from '@/types';

interface ProgramFormProps {
  initialData?: Program | null;
  onSubmit: (data: Partial<Program>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ProgramForm: React.FC<ProgramFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { entities: divisions } = useSelector((state: RootState) => state.divisions);
  const [users, setUsers] = useState<User[]>([]);
  const [manualEntries, setManualEntries] = useState<string[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    divisionId: '',
    leadId: '',
    leadText: '',
    leadType: 'USER' as LeadType,
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch divisions on component mount
  useEffect(() => {
    dispatch(fetchDivisions());
  }, [dispatch]);

  // Fetch users and manual entries with PROGRAM_LEAD_SCOPE on component mount
  useEffect(() => {
    const fetchProgramLeads = async () => {
      setUsersLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get('/api/v1/user-scopes/suggestions?scope=PROGRAM_LEAD_SCOPE', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const suggestions = response.data.data.suggestions || [];

          // Extract USER type suggestions and convert to User format
          const userSuggestions = suggestions
            .filter((s: any) => s.type === 'USER')
            .map((s: any) => ({
              id: s.id,
              firstName: s.label.split(' ')[0],
              lastName: s.label.split(' ').slice(1).join(' '),
              email: s.email
            }));

          // Extract manual TEXT entries
          const manualSuggestions = suggestions
            .filter((s: any) => s.type === 'TEXT')
            .map((s: any) => s.label);

          setUsers(userSuggestions);
          setManualEntries(manualSuggestions);
        }
      } catch (error) {
        console.error('Failed to fetch program leads:', error);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchProgramLeads();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description || '',
        divisionId: initialData.divisionId || '',
        leadId: initialData.leadId || '',
        leadText: initialData.leadText || '',
        leadType: initialData.leadType || 'USER',
        isActive: initialData.isActive
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Program name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Program name must be at least 3 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Program name must not exceed 100 characters';
    }

    // Division is required
    if (!formData.divisionId) {
      newErrors.divisionId = 'Division is required';
    }

    // Category is automatically set to TECH, no validation needed

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      name: formData.name,
      description: formData.description || undefined,
      divisionId: formData.divisionId,
      category: 'TECH' as const, // Always TECH for programs
      leadType: formData.leadType,
      isActive: formData.isActive,
      ...(formData.leadType === 'USER'
        ? { leadId: formData.leadId || undefined, leadText: undefined }
        : { leadId: undefined, leadText: formData.leadText || undefined }
      )
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLeadChange = (value: string, type: 'USER' | 'TEXT') => {
    setFormData(prev => ({
      ...prev,
      leadType: type,
      leadId: type === 'USER' ? value : '',
      leadText: type === 'TEXT' ? value : ''
    }));
    
    // Clear lead-related errors
    if (errors.leadId || errors.leadText) {
      setErrors(prev => ({ 
        ...prev, 
        leadId: '', 
        leadText: '' 
      }));
    }
  };

  // Prepare user options for ComboBox - includes both users and manual entries
  const userOptions = [
    // User suggestions
    ...users.map(user => ({
      id: user.id,
      label: `${user.firstName} ${user.lastName}`,
      email: user.email
    })),
    // Manual entry suggestions with indicator
    ...manualEntries.map(entry => ({
      id: entry, // Use the text itself as ID for manual entries
      label: entry,
      email: '(Previously used)'
    }))
  ];

  // Get current lead value for ComboBox
  const getCurrentLeadValue = () => {
    if (formData.leadType === 'USER' && formData.leadId) {
      return formData.leadId;
    } else if (formData.leadType === 'TEXT' && formData.leadText) {
      return formData.leadText;
    }
    return '';
  };

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  const divisionOptions = [
    { value: '', label: 'Select Division...' },
    ...divisions.map(div => ({ value: div.id, label: div.name }))
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Input
            label="Program Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={errors.name}
            placeholder="e.g., Customer Platform Engineering"
            required
            fullWidth
          />
        </div>

        <Select
          label="Division"
          value={formData.divisionId}
          onChange={(e) => handleInputChange('divisionId', e.target.value)}
          options={divisionOptions}
          error={errors.divisionId}
          required
          fullWidth
          helperText="The organizational division this program belongs to"
        />

        <div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-foreground">
              Category
            </label>
            <div className="h-9 px-3 flex items-center bg-muted/50 border border-border rounded-md">
              <span className="text-sm text-foreground">Tech</span>
            </div>
            <p className="text-xs text-muted-foreground">
              All programs are technology-focused delivery units
            </p>
          </div>
        </div>

        <Select
          label="Status"
          value={formData.isActive.toString()}
          onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
          options={statusOptions}
          fullWidth
          helperText="Active programs can receive allocations and manage teams"
        />

        <div className="md:col-span-2">
          <ComboBox
            label="Program Lead"
            options={userOptions}
            value={getCurrentLeadValue()}
            onChange={handleLeadChange}
            placeholder="Select a user or enter manually..."
            allowFreeText={true}
            disabled={usersLoading}
            error={errors.leadId || errors.leadText}
            className="w-full"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">
            The person responsible for this program's delivery. You can select from existing users or enter manually.
          </p>
        </div>

        <div className="md:col-span-2">
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <label className="text-sm font-medium text-foreground">
                Description
              </label>
              <span className={`text-xs tabular-nums ${formData.description.length > 500 ? 'text-status-error font-medium' : 'text-muted-foreground'}`}>
                {formData.description.length}/500
              </span>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the program's purpose, scope, and objectives..."
              rows={4}
              className="block w-full min-h-[96px] rounded-md border bg-muted px-3 py-2 text-sm text-foreground placeholder-muted-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent border-border hover:border-[var(--border)]"
            />
            {errors.description && (
              <p className="text-xs text-status-error font-medium">{errors.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {initialData ? 'Update' : 'Create'} Program
        </Button>
      </div>
    </form>
  );
};

export default ProgramForm;